import React, { useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { useUsersApi } from "../../services/users";
import { notifySuccess } from "../../services/notify";
import {
  EMPTY_PASSWORD,
  GeneratedPassword,
  PasswordChoice,
  passwordToSend,
} from "./PasswordFields";

const EMPTY = { email: "", name: "", role: "user" };

export const AddUserModal = ({ isOpen, toggle, onAdded }) => {
  const usersApi = useUsersApi();
  const [fields, setFields] = useState(EMPTY);
  const [password, setPassword] = useState(EMPTY_PASSWORD);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Set once the account exists and its password was generated.
  const [created, setCreated] = useState(null);

  const setField = (name) => (event) => {
    setFields({ ...fields, [name]: event.target.value });
    setError(null);
  };

  // Clears everything, so no password outlives the dialog.
  const finish = () => {
    setFields(EMPTY);
    setPassword(EMPTY_PASSWORD);
    setError(null);
    setCreated(null);
    toggle();
  };

  // What the user can trigger; not while a request is in flight.
  const close = () => {
    if (!submitting) finish();
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const chosen = passwordToSend(password);
    if (chosen.error) {
      setError(chosen.error);
      return;
    }

    setSubmitting(true);
    let result;
    try {
      result = await usersApi.add({
        email: fields.email.trim(),
        name: fields.name.trim() || null,
        role: fields.role,
        password: chosen.password,
      });
    } catch (caught) {
      setError(caught.message);
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onAdded();

    if (result.generatedPassword) {
      setCreated({ email: fields.email.trim(), password: result.generatedPassword });
    } else {
      notifySuccess(`Added ${fields.email.trim()}`);
      finish();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={close} centered>
      {created ? (
        <>
          <ModalHeader toggle={close}>User added</ModalHeader>
          <ModalBody style={{ fontSize: "13px" }}>
            <GeneratedPassword email={created.email} password={created.password} />
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={close}
              style={{ background: "#4A42A0", borderColor: "#4A42A0" }}
            >
              Done
            </Button>
          </ModalFooter>
        </>
      ) : (
        <Form onSubmit={onSubmit} data-testid="add-user-form">
          <ModalHeader toggle={close}>Add user</ModalHeader>
          <ModalBody style={{ fontSize: "13px" }}>
            <FormGroup>
              <Label for="add-user-email">Email</Label>
              <Input
                id="add-user-email"
                type="email"
                autoComplete="off"
                value={fields.email}
                onChange={setField("email")}
                disabled={submitting}
                autoFocus
              />
            </FormGroup>
            <FormGroup>
              <Label for="add-user-name">Name (optional)</Label>
              <Input
                id="add-user-name"
                autoComplete="off"
                value={fields.name}
                onChange={setField("name")}
                disabled={submitting}
              />
            </FormGroup>
            <FormGroup>
              <Label for="add-user-role">Role</Label>
              <Input
                id="add-user-role"
                type="select"
                value={fields.role}
                onChange={setField("role")}
                disabled={submitting}
              >
                <option value="user">User — every module</option>
                <option value="admin">Admin — every module, Analytics and Users</option>
              </Input>
            </FormGroup>
            <PasswordChoice
              value={password}
              onChange={(value) => {
                setPassword(value);
                setError(null);
              }}
              disabled={submitting}
            />
            {error ? (
              <Alert
                color="danger"
                className="mt-3 mb-0"
                style={{ fontSize: "13px" }}
                data-testid="add-user-error"
              >
                {error}
              </Alert>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!fields.email.trim() || submitting}
              style={{ background: "#4A42A0", borderColor: "#4A42A0" }}
            >
              {submitting ? "Adding..." : "Add user"}
            </Button>
          </ModalFooter>
        </Form>
      )}
    </Modal>
  );
};
